import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  DatabaseZap, 
  Download, 
  Filter, 
  MessageCircle, 
  Check,
  Loader2,
  X
} from 'lucide-react';

// Firebase hooks
import { useConversations } from '../../hooks/useConversations';
import { useMessageHistory } from '../../hooks/useMessageStorage';

// Utility functions
import { processMessages, filterConversations, sortConversations } from '../../components/ChatHistoryViewer/utils/messageProcessor';
import {
  downloadFile,
  prepareJSONExport,
  prepareTextExport,
  convertToCSV,
  generateExportFilename
} from '../../components/ChatHistoryViewer/utils/exportHelpers';

// Components
import ConversationList from '../../components/ChatHistoryViewer/components/ConversationList';
import MessageList from '../../components/ChatHistoryViewer/components/MessageList';
import ExportOptions from '../../components/ChatHistoryViewer/components/ExportOptions';
import FilterOptions from '../../components/ChatHistoryViewer/components/FilterOptions';
import DeleteConfirmation from '../../components/ChatHistoryViewer/components/DeleteConfirmation';

/**
 * Main ChatHistoryViewer component with improved mobile/desktop design,
 * functioning filters, and favorite functionality
 */
const ChatHistoryViewer = ({ userId }) => {
  // Core state
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messagesMap, setMessagesMap] = useState({});
  const [favorites, setFavorites] = useState([]);
  
  // UI state
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  
  // Refs
  const exportTimeoutRef = useRef(null);
  const exportButtonRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const navigate = useNavigate();
  
  // Firebase data hooks
  const { 
    conversations, 
    loading: conversationsLoading,
    deleteConversation: deleteConversationFromFirebase,
    updateConversation
  } = useConversations(userId);
  
  // Get raw messages from the hook
  const { 
    messages: rawMessages, 
    loading: messagesLoading 
  } = useMessageHistory(userId, selectedConversation?.id);

  // Load favorites from local storage on mount
  useEffect(() => {
    const storedFavorites = localStorage.getItem(`chat-favorites-${userId}`);
    if (storedFavorites) {
      try {
        setFavorites(JSON.parse(storedFavorites));
      } catch (e) {
        console.error('Error parsing favorites from localStorage:', e);
      }
    }
  }, [userId]);

  // Save favorites to local storage when changed
  useEffect(() => {
    if (favorites.length > 0 || localStorage.getItem(`chat-favorites-${userId}`)) {
      localStorage.setItem(`chat-favorites-${userId}`, JSON.stringify(favorites));
    }
  }, [favorites, userId]);

  // Process messages when they come in
  useEffect(() => {
    if (!messagesLoading && rawMessages.length > 0 && selectedConversation?.id) {
      const processed = processMessages(rawMessages);
      setMessagesMap(prev => ({
        ...prev,
        [selectedConversation.id]: processed
      }));
    }
  }, [rawMessages, messagesLoading, selectedConversation]);

  // Get the current conversation's messages from our processed map
  const conversationMessages = messagesMap[selectedConversation?.id] || [];

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Toggle favorite status for a conversation
  const handleToggleFavorite = useCallback((conversation) => {
    setFavorites(prev => {
      if (prev.includes(conversation.id)) {
        return prev.filter(id => id !== conversation.id);
      } else {
        return [...prev, conversation.id];
      }
    });
  }, []);

  // Toggle show favorites only filter
  const handleToggleFavoritesOnly = useCallback(() => {
    setShowFavoritesOnly(prev => !prev);
  }, []);

  // Delete conversation
  const handleDeleteConversation = useCallback(async (conversation) => {
    try {
      await deleteConversationFromFirebase(conversation.id);
      
      // Also remove from favorites if it's there
      if (favorites.includes(conversation.id)) {
        setFavorites(prev => prev.filter(id => id !== conversation.id));
      }
      
      if (selectedConversation?.id === conversation.id) {
        setSelectedConversation(null);
      }
      
      setConfirmDelete(null);
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  }, [deleteConversationFromFirebase, favorites, selectedConversation]);

  // Archive conversation (placeholder for now)
  const handleArchiveConversation = useCallback((conversation) => {
    console.log('Archive conversation:', conversation.id);
    // Implementation would go here
  }, []);

  // Conversation selection handler
  const handleSelectConversation = useCallback((conversation) => {
    setSelectedConversation(conversation);
    
    // Clear the messages map for this conversation to force a refresh if needed
    if (conversation && (!messagesMap[conversation.id] || messagesMap[conversation.id].length === 0)) {
      setMessagesMap(prev => ({
        ...prev,
        [conversation.id]: undefined
      }));
    }
  }, [messagesMap]);

  // Toggle export options with position calculation
  const toggleExportOptions = useCallback(() => {
    if (exportButtonRef.current) {
      const rect = exportButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 5,
        right: window.innerWidth - rect.right - window.scrollX
      });
      setShowExportOptions(!showExportOptions);
    }
  }, [showExportOptions]);

  // Export conversation
  const handleExportConversation = useCallback((format) => {
    if (!selectedConversation || !conversationMessages.length) return;
    
    setIsExporting(true);
    setShowExportOptions(false);
    
    // Small delay to show loading state
    setTimeout(() => {
      const fileName = generateExportFilename(selectedConversation);
      
      try {
        if (format === 'text') {
          const content = prepareTextExport(selectedConversation, conversationMessages);
          downloadFile(content, `${fileName}.txt`, 'text/plain');
        } else if (format === 'json') {
          const content = JSON.stringify(
            prepareJSONExport(selectedConversation, conversationMessages), 
            null, 2
          );
          downloadFile(content, `${fileName}.json`, 'application/json');
        } else if (format === 'csv') {
          const content = convertToCSV(conversationMessages);
          downloadFile(content, `${fileName}.csv`, 'text/csv');
        }
        
        setIsExporting(false);
        setExportSuccess(true);
        
        // Reset success state after 3 seconds
        if (exportTimeoutRef.current) clearTimeout(exportTimeoutRef.current);
        exportTimeoutRef.current = setTimeout(() => {
          setExportSuccess(false);
        }, 3000);
        
      } catch (error) {
        console.error('Export error:', error);
        setIsExporting(false);
      }
    }, 800);
  }, [selectedConversation, conversationMessages]);

  // Export all conversations
  const handleExportAllConversations = useCallback((format) => {
    setIsExporting(true);
    setShowExportOptions(false);
    
    setTimeout(() => {
      const timestamp = new Date().toISOString().split('T')[0];
      const fileName = `chat-all-conversations-${timestamp}`;
      
      try {
        if (format === 'json') {
          // For JSON, create a structured export of conversation metadata
          const content = JSON.stringify(
            {
              exportDate: new Date().toISOString(),
              conversations: conversations.map(conv => ({
                id: conv.id,
                title: conv.metadata?.title || 'Untitled Chat',
                createdAt: conv.metadata?.createdAt,
                updatedAt: conv.metadata?.updatedAt,
                messageCount: conv.metadata?.messageCount || 0,
                lastMessage: conv.lastMessage,
                isFavorite: favorites.includes(conv.id)
              }))
            }, 
            null, 2
          );
          downloadFile(content, `${fileName}.json`, 'application/json');
        } else if (format === 'csv') {
          // For CSV, create a list of conversations
          const header = 'ID,Title,Created,Updated,Message Count,Last Message,Favorite\n';
          const rows = conversations.map(conv => {
            const id = conv.id;
            const title = `"${(conv.metadata?.title || 'Untitled Chat').replace(/"/g, '""')}"`;
            const created = conv.metadata?.createdAt;
            const updated = conv.metadata?.updatedAt;
            const messageCount = conv.metadata?.messageCount || 0;
            const lastMessage = `"${(conv.lastMessage || '').replace(/"/g, '""').replace(/\n/g, ' ')}"`;
            const isFavorite = favorites.includes(conv.id) ? 'Yes' : 'No';
            return `${id},${title},${created},${updated},${messageCount},${lastMessage},${isFavorite}`;
          }).join('\n');
          
          downloadFile(header + rows, `${fileName}.csv`, 'text/csv');
        }
        
        setIsExporting(false);
        setExportSuccess(true);
        
        // Reset success state after 3 seconds
        if (exportTimeoutRef.current) clearTimeout(exportTimeoutRef.current);
        exportTimeoutRef.current = setTimeout(() => {
          setExportSuccess(false);
        }, 3000);
        
      } catch (error) {
        console.error('Export error:', error);
        setIsExporting(false);
      }
    }, 1000);
  }, [conversations, favorites]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (exportTimeoutRef.current) {
        clearTimeout(exportTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="md:pl-16 pt-4 px-4 pb-4 min-h-screen bg-black relative overflow-hidden">
      {/* Glass overlay effect */}
      <div className="fixed inset-0 bg-gradient-to-b from-white/[0.02] to-transparent pointer-events-none" />

      {/* Header Section */}
      <div className="relative max-w-screen-xl mx-auto mb-4">
        {/* Spacer for mobile banner - only on mobile */}
        <div className="md:hidden h-16"></div>

        <div className="bg-white/[0.02] backdrop-blur-sm rounded-lg p-4 md:p-6 border border-white/[0.05]">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Terminal-style header - hidden on mobile, shown on desktop */}
            <div className="hidden md:flex items-center gap-4">
              <div className="p-3 md:p-4 bg-white/[0.03] backdrop-blur-sm rounded-md border border-white/[0.05] hover:bg-white/[0.05] hover:border-white/[0.08] transition-all duration-200">
                <DatabaseZap className="w-6 h-6 md:w-7 md:h-7 text-emerald-400/80" />
              </div>
              <div>
                <h3 className="text-[10px] font-medium tracking-[0.3em] text-white/30 uppercase mb-2">
                  CHAT HISTORY
                </h3>
                <h1 className="text-xl md:text-2xl font-sans text-white/95">
                  Conversation Archive
                </h1>
                <p className="text-xs md:text-sm text-white/50 mt-1">
                  Browse and export your chat history
                </p>
              </div>
            </div>
            
            {/* Premium action buttons */}
            <div className="flex items-center gap-2">
              {/* Export Button - Secondary glass style */}
              <div className="relative" ref={exportButtonRef}>
                <button
                  className={`px-4 py-2.5 bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] rounded-md flex items-center gap-2 transition-all duration-200 ${
                    isExporting || exportSuccess
                      ? 'opacity-50 pointer-events-none'
                      : 'hover:bg-white/[0.05] hover:border-white/[0.08]'
                  }`}
                  onClick={toggleExportOptions}
                  disabled={isExporting || exportSuccess}
                >
                  {isExporting ? (
                    <Loader2 className="w-4 h-4 text-white/50 animate-spin" />
                  ) : exportSuccess ? (
                    <Check className="w-4 h-4 text-emerald-400/80" />
                  ) : (
                    <Download className="w-4 h-4 text-white/50" />
                  )}
                  <span className="text-[11px] text-white/70 uppercase tracking-wider hidden sm:inline">
                    Export
                  </span>
                </button>
              </div>

              {/* Filter Button - Active state with accent */}
              <button
                className={`px-4 py-2.5 backdrop-blur-sm rounded-md flex items-center gap-2 transition-all duration-200 ${
                  showFilters
                    ? 'bg-emerald-500/[0.08] border border-emerald-500/20 text-emerald-400/80'
                    : 'bg-white/[0.02] border border-white/[0.05] text-white/50 hover:bg-white/[0.05] hover:border-white/[0.08]'
                }`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="w-4 h-4" />
                <span className="text-[11px] uppercase tracking-wider hidden sm:inline">
                  Filter
                </span>
              </button>

              {/* New Chat Button - Primary gradient CTA */}
              <button
                className="px-5 py-2.5 bg-gradient-to-r from-emerald-500/80 to-violet-500/80 backdrop-blur-sm rounded-md text-white text-[11px] font-medium tracking-[0.15em] uppercase hover:from-emerald-500 hover:to-violet-500 transition-all duration-200 flex items-center gap-2 shadow-lg shadow-emerald-500/10"
                onClick={() => navigate('/')}
              >
                <MessageCircle className="w-4 h-4" />
                <span className="hidden sm:inline">New Chat</span>
              </button>
            </div>
          </div>
          
          {/* Filter options panel */}
          <FilterOptions 
            isVisible={showFilters}
            sortBy={sortBy}
            onSortChange={setSortBy}
            favorites={favorites}
            onToggleFavoritesOnly={handleToggleFavoritesOnly}
            showFavoritesOnly={showFavoritesOnly}
          />
        </div>

        {/* Main Content Area - Premium glass card */}
        <div className="bg-white/[0.02] backdrop-blur-sm rounded-lg border border-white/[0.05] overflow-hidden h-[calc(100vh-180px)] md:h-[calc(100vh-200px)]">
          <div className="flex flex-col md:flex-row h-full">
            {/* Conversations List - Only hide on mobile when conversation is selected */}
            <div className={`${selectedConversation && isMobile ? 'hidden md:block' : 'block'} md:w-80 md:block h-full`}>
              <ConversationList 
                conversations={conversations}
                loading={conversationsLoading}
                selectedConversationId={selectedConversation?.id}
                onSelectConversation={handleSelectConversation}
                onNewChat={() => navigate('/')}
                onDeleteConversation={(conversation) => setConfirmDelete(conversation)}
                onArchiveConversation={handleArchiveConversation}
                onToggleFavorite={handleToggleFavorite}
                favorites={favorites}
                sortBy={sortBy}
                showFavoritesOnly={showFavoritesOnly}
              />
            </div>

            {/* Messages View */}
            <div className={`${!selectedConversation && isMobile ? 'hidden' : 'flex'} flex-1 flex flex-col h-full`}>
              <MessageList 
                conversation={selectedConversation}
                messages={conversationMessages}
                loading={messagesLoading}
                onBack={() => setSelectedConversation(null)}
                onDelete={(conversation) => setConfirmDelete(conversation)}
                onShare={() => toggleExportOptions()}
                onToggleFavorite={handleToggleFavorite}
                isFavorite={selectedConversation ? favorites.includes(selectedConversation.id) : false}
                isMobile={isMobile}
              />
            </div>
          </div>
        </div>
        
        {/* Export Options Dropdown - Rendered at the document level to be above all other elements */}
        <ExportOptions 
          isVisible={showExportOptions}
          onClose={() => setShowExportOptions(false)}
          selectedConversation={selectedConversation}
          onExportConversation={handleExportConversation}
          onExportAllConversations={handleExportAllConversations}
          position={dropdownPosition}
        />
        
        {/* Delete Confirmation Modal */}
        <DeleteConfirmation 
          isVisible={!!confirmDelete}
          conversation={confirmDelete}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={handleDeleteConversation}
        />
        
        {/* Premium Export Success Toast */}
        <AnimatePresence>
          {exportSuccess && (
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-black/90 backdrop-blur-lg border border-emerald-500/20 rounded-lg py-3 px-4 flex items-center gap-3 shadow-2xl z-50"
            >
              <div className="rounded-full bg-emerald-500/[0.08] border border-emerald-500/20 p-2">
                <Check className="w-4 h-4 text-emerald-400/80" />
              </div>
              <div>
                <p className="text-white/80 text-sm font-medium">Export completed</p>
                <p className="text-white/40 text-xs mt-0.5">File downloaded successfully</p>
              </div>
              <button
                className="ml-2 p-1.5 hover:bg-white/[0.05] rounded-md transition-colors"
                onClick={() => setExportSuccess(false)}
              >
                <X className="w-4 h-4 text-white/50 hover:text-white/70" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ChatHistoryViewer;